import { uuidV7 } from './ids.js';

/**
 * One idempotency key per user intent (AC §7.1, FTD §14.3).
 *
 * A `CommandIntent` stands for one thing the person decided to do (confirm this sale, pay these
 * installments). While the payload stays the same the intent keeps its key, so a transport retry or a
 * "Check status" after a timeout reuses it. When the person edits the payload, it is a different
 * request, and the intent issues a new key (the old key would answer `409 IDEMPOTENCY_KEY_REUSED`).
 * After a definitive outcome, call {@link CommandIntent.complete}; the next submission is a new intent.
 */
export class CommandIntent {
  readonly #generateKey: () => string;
  #fingerprint: string | undefined;
  #key: string | undefined;

  constructor(
    /** A name for logs and tests only, for example `sale.confirm`. */
    readonly name: string,
    generateKey: () => string = () => uuidV7(),
  ) {
    this.#generateKey = generateKey;
  }

  /** The key currently bound to the intent, if a request was already sent. */
  get key(): string | undefined {
    return this.#key;
  }

  /** Returns the key for this payload: the same key for the same payload, a new key otherwise. */
  keyFor(payload: unknown): string {
    const fingerprint = canonicalJson(payload);

    if (this.#key === undefined || this.#fingerprint !== fingerprint) {
      this.#key = this.#generateKey();
      this.#fingerprint = fingerprint;
    }

    return this.#key;
  }

  /** Ends the intent after a definitive server outcome; the next `keyFor` starts a new intent. */
  complete(): void {
    this.#key = undefined;
    this.#fingerprint = undefined;
  }
}

export function createIntent(name: string): CommandIntent {
  return new CommandIntent(name);
}

/**
 * Canonical JSON with recursively sorted keys and no whitespace (the same shape the server hashes,
 * AC §7.1). `undefined` members are dropped as `JSON.stringify` does.
 */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(sortValue(value)) ?? 'undefined';
}

function sortValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortValue);
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.keys(value)
        .sort()
        .map((key) => [key, sortValue((value as Record<string, unknown>)[key])]),
    );
  }
  return value;
}
